import React, { useState, useEffect } from 'react';

function FilterSection({ 
  filters, 
  onFilterChange, 
  onClearFilters,
  showLocationFilters = true,
  selectedSpecialty = null,
  onSpecialtyChange = null,
  loading = false
}) {
  // Specialty search states
  const [specialtySearch, setSpecialtySearch] = useState('');
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);
  
  // Mobile filter collapse state
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const allSpecialties = ['Anxiety', 'Depression', 'Couples Therapy', 'ADHD', 'Trauma', 'Teen Therapy', 'Family Therapy', 'Addiction', 'Grief', 'Eating Disorders', 'PTSD', 'OCD', 'Bipolar Disorder', 'Autism Spectrum', 'Substance Abuse', 'Anger Management', 'Life Coaching', 'Career Counseling', 'Relationship Issues', 'Self-Esteem'];
  
  // Filter specialties based on search
  const filteredSpecialties = allSpecialties.filter(specialty =>
    specialty.toLowerCase().includes(specialtySearch.toLowerCase())
  );

  const insuranceOptions = ['', 'Aetna', 'Blue Cross Blue Shield', 'Cigna', 'UnitedHealth', 'Kaiser Permanente', 'Humana', 'Medicare', 'Medicaid'];
  const genderOptions = ['All', 'Male', 'Female', 'Non-binary'];
  
  const sessionTypes = [
    { 
      value: '', 
      label: 'All'
    },
    { 
      value: 'in-person', 
      label: 'In-Person'
    },
    { 
      value: 'telehealth', 
      label: 'Online'
    },
    { 
      value: 'both', 
      label: 'Both'
    }
  ];

  const professionalTypes = [
    {
      value: '',
      label: 'All Types'
    },
    {
      value: 'Therapist',
      label: 'Therapist'
    },
    {
      value: 'Psychologist',
      label: 'Psychologist'
    },
    {
      value: 'Psychiatrist',
      label: 'Psychiatrist'
    }
  ];

  const usStates = [
    '', 'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 
    'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 
    'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];
  
  const popularCities = [
    '', 'New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego', 
    'Dallas', 'San Jose', 'Austin', 'Jacksonville', 'Fort Worth', 'Columbus', 'Charlotte', 'San Francisco', 
    'Indianapolis', 'Seattle', 'Denver', 'Boston', 'El Paso', 'Nashville', 'Detroit', 'Oklahoma City', 
    'Portland', 'Las Vegas', 'Memphis', 'Louisville', 'Baltimore', 'Milwaukee', 'Albuquerque', 'Tucson', 
    'Fresno', 'Sacramento', 'Kansas City', 'Mesa', 'Atlanta', 'Colorado Springs', 'Raleigh', 'Omaha', 
    'Miami', 'Oakland', 'Minneapolis', 'Tulsa', 'Cleveland', 'Wichita', 'Arlington', 'New Orleans'
  ];

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    
    // Count specialty filter
    const hasSpecialty = selectedSpecialty && selectedSpecialty !== 'All' ? true : (filters?.specialty && filters.specialty !== '');
    if (hasSpecialty) count++;
    
    // Count other filters
    if (filters.sessionType && filters.sessionType !== '') count++;
    if (filters.professionalType && filters.professionalType !== '') count++;
    if (filters.gender && filters.gender !== '') count++;
    if (filters.insurance && filters.insurance !== '') count++;
    if (showLocationFilters) {
      if (filters.state && filters.state !== '') count++;
      if (filters.city && filters.city !== '') count++;
    }
    if (filters.radius && filters.radius !== '50') count++; // Count non-default radius
    
    return count;
  };

  // Get active filter labels for display
  const getActiveFilterLabels = () => {
    const labels = [];
    
    // Add specialty
    const hasSpecialty = selectedSpecialty && selectedSpecialty !== 'All' ? selectedSpecialty : (filters?.specialty && filters.specialty !== '' ? filters.specialty : null);
    if (hasSpecialty) labels.push(hasSpecialty);
    
    // Add other active filters
    if (filters.sessionType && filters.sessionType !== '') {
      const sessionType = sessionTypes.find(st => st.value === filters.sessionType);
      if (sessionType) labels.push(sessionType.label);
    }
    if (filters.professionalType && filters.professionalType !== '') labels.push(filters.professionalType);
    if (filters.gender && filters.gender !== '') labels.push(filters.gender);
    if (filters.insurance && filters.insurance !== '') labels.push(filters.insurance);
    
    return labels.slice(0, 3); // Show max 3 labels
  };

  // Handle specialty selection for searchable dropdown
  const handleSpecialtySelect = (specialty) => {
    if (onSpecialtyChange) {
      // For CityPage - use the specialty change handler
      onSpecialtyChange(specialty || 'All');
    } else {
      // For FindTherapistsPage - use filter change
      onFilterChange('specialty', specialty);
    }
    setSpecialtySearch(specialty);
    setShowSpecialtyDropdown(false);
  };

  // Handle specialty search input
  const handleSpecialtySearch = (value) => {
    setSpecialtySearch(value);
    if (!onSpecialtyChange) {
      // For FindTherapistsPage
      onFilterChange('specialty', value);
    }
    setShowSpecialtyDropdown(true);
  };

  // Initialize specialty search from selected specialty
  useEffect(() => {
    if (selectedSpecialty && selectedSpecialty !== 'All' && !specialtySearch) {
      setSpecialtySearch(selectedSpecialty);
    } else if (filters?.specialty && !specialtySearch) {
      setSpecialtySearch(filters.specialty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpecialty, filters?.specialty]);

  // Clear all filters
  const handleClearAll = () => {
    setSpecialtySearch('');
    setShowSpecialtyDropdown(false);
    onClearFilters();
  };

  // Filter content component
  const FilterContent = () => (
    <>
      {/* Searchable Specialty Filter */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-[#254e70] mb-2" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
          Specialty
        </label>
        <div className="relative">
          <input
            type="text"
            value={specialtySearch}
            onChange={(e) => handleSpecialtySearch(e.target.value)}
            onFocus={() => setShowSpecialtyDropdown(true)}
            onBlur={() => setTimeout(() => setShowSpecialtyDropdown(false), 200)}
            placeholder="Search specialties (e.g., Anxiety, Depression, ADHD...)"
            className="w-full px-4 py-3 border border-[#254e70]/20 rounded-lg text-[#254e70] focus:border-[#254e70] focus:ring-2 focus:ring-[#254e70]/10 outline-none"
            style={{ fontFamily: 'Nunito Sans, sans-serif' }}
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg className="w-5 h-5 text-[#254e70]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          
          {/* Dropdown Results */}
          {showSpecialtyDropdown && (filteredSpecialties.length > 0 || specialtySearch) && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-[#254e70]/20 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {specialtySearch && (
                <button
                  onClick={() => handleSpecialtySelect('')}
                  className="w-full px-4 py-3 text-left hover:bg-[#e4fde1] transition-colors border-b border-[#254e70]/10"
                  style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                >
                  <span className="text-[#254e70]/60">Clear specialty filter</span>
                </button>
              )}
              {filteredSpecialties.map((specialty) => (
                <button
                  key={specialty}
                  onClick={() => handleSpecialtySelect(specialty)}
                  className="w-full px-4 py-3 text-left hover:bg-[#e4fde1] transition-colors text-[#254e70]"
                  style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                >
                  {specialty}
                </button>
              ))}
              {filteredSpecialties.length === 0 && specialtySearch && (
                <div className="px-4 py-3 text-[#254e70]/60" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                  No specialties found matching "{specialtySearch}"
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Session Type Filter */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-[#254e70] mb-3" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
          Session Type
        </label>
        <div className="flex flex-wrap gap-2">
          {sessionTypes.map((sessionType) => (
            <label
              key={sessionType.value}
              className="relative cursor-pointer"
            >
              <input
                type="radio"
                name="sessionType"
                value={sessionType.value}
                checked={filters.sessionType === sessionType.value}
                onChange={() => onFilterChange('sessionType', sessionType.value)}
                className="sr-only"
              />
              <div className={`px-3 py-2 rounded-full border-2 transition-all duration-200 text-xs font-medium ${
                filters.sessionType === sessionType.value
                  ? 'border-[#254e70] bg-[#254e70] text-white shadow-lg'
                  : 'border-[#254e70]/20 bg-white text-[#254e70] hover:border-[#254e70]/40 hover:shadow-md'
              }`} style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                {sessionType.label}
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Professional Type Filter */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-[#254e70] mb-3" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
          Professional Type
        </label>
        <div className="flex flex-wrap gap-2">
          {professionalTypes.map((type) => (
            <label
              key={type.value}
              className="relative cursor-pointer"
            >
              <input
                type="radio"
                name="professionalType"
                value={type.value}
                checked={filters.professionalType === type.value}
                onChange={() => onFilterChange('professionalType', type.value)}
                className="sr-only"
              />
              <div className={`px-3 py-2 rounded-full border-2 transition-all duration-200 text-xs font-medium ${
                filters.professionalType === type.value
                  ? 'border-[#254e70] bg-[#254e70] text-white shadow-lg'
                  : 'border-[#254e70]/20 bg-white text-[#254e70] hover:border-[#254e70]/40 hover:shadow-md'
              }`} style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                {type.label}
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Gender Filter */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-[#254e70] mb-3" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
          Gender Preference
        </label>
        <div className="flex flex-wrap gap-2">
          {genderOptions.map((gender) => (
            <label
              key={gender}
              className="relative cursor-pointer"
            >
              <input
                type="radio"
                name="gender"
                value={gender === 'All' ? '' : gender}
                checked={(filters.gender === '' && gender === 'All') || filters.gender === gender}
                onChange={() => onFilterChange('gender', gender === 'All' ? '' : gender)}
                className="sr-only"
              />
              <div className={`px-3 py-2 rounded-full border-2 transition-all duration-200 text-xs font-medium ${
                (filters.gender === '' && gender === 'All') || filters.gender === gender
                  ? 'border-[#254e70] bg-[#254e70] text-white shadow-lg'
                  : 'border-[#254e70]/20 bg-white text-[#254e70] hover:border-[#254e70]/40 hover:shadow-md'
              }`} style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                {gender}
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Location Filters - Only show if enabled */}
      {showLocationFilters && (
        <>
          {/* State Filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#254e70] mb-2">State</label>
            <select
              value={filters.state}
              onChange={(e) => onFilterChange('state', e.target.value)}
              className="w-full px-3 py-2 border border-[#254e70]/20 rounded-lg text-[#254e70] text-sm focus:border-[#254e70] focus:ring-1 focus:ring-[#254e70] outline-none"
            >
              <option value="">All States</option>
              {usStates.slice(1).map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>

          {/* City Filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#254e70] mb-2">City</label>
            <select
              value={filters.city}
              onChange={(e) => onFilterChange('city', e.target.value)}
              className="w-full px-3 py-2 border border-[#254e70]/20 rounded-lg text-[#254e70] text-sm focus:border-[#254e70] focus:ring-1 focus:ring-[#254e70] outline-none"
            >
              <option value="">All Cities</option>
              {popularCities.slice(1).map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {/* Search Radius Filter */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-[#254e70] mb-2">Search Radius</label>
        <select
          value={filters.radius}
          onChange={(e) => onFilterChange('radius', e.target.value)}
          className="w-full px-3 py-2 border border-[#254e70]/20 rounded-lg text-[#254e70] text-sm focus:border-[#254e70] focus:ring-1 focus:ring-[#254e70] outline-none"
        >
          <option value="10">10 miles</option>
          <option value="25">25 miles</option>
          <option value="50">50 miles</option>
          <option value="100">100 miles</option>
        </select>
      </div>

      {/* Insurance Filter */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-[#254e70] mb-2">Insurance</label>
        <select
          value={filters.insurance}
          onChange={(e) => onFilterChange('insurance', e.target.value)}
          className="w-full px-3 py-2 border border-[#254e70]/20 rounded-lg text-[#254e70] text-sm focus:border-[#254e70] focus:ring-1 focus:ring-[#254e70] outline-none"
        >
          <option value="">All Insurance</option>
          {insuranceOptions.slice(1).map(insurance => (
            <option key={insurance} value={insurance}>{insurance}</option>
          ))}
        </select>
      </div>
    </>
  );

  const activeFilterCount = getActiveFilterCount();
  const activeFilterLabels = getActiveFilterLabels();

  return (
    <>
      {/* Mobile Filter Toggle Button */}
      <div className="lg:hidden mb-6">
        <button
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          className="w-full flex items-center justify-between bg-white border border-[#254e70]/20 rounded-xl px-4 py-3 text-[#254e70] font-medium shadow-sm hover:shadow-md transition-all"
          style={{ fontFamily: 'Nunito Sans, sans-serif' }}
        >
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
            </svg>
            <span>
              Filters {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 bg-[#254e70] text-white text-xs font-bold rounded-full px-1.5">
                  {activeFilterCount}
                </span>
              )}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {activeFilterLabels.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-[#254e70]/70">
                {activeFilterLabels.map((label, index) => (
                  <span key={index} className="bg-[#e4fde1] px-2 py-1 rounded">
                    {label}
                  </span>
                ))}
                {activeFilterCount > 3 && (
                  <span className="text-[#254e70]/50">+{activeFilterCount - 3}</span>
                )}
              </div>
            )}
            
            <svg 
              className={`w-5 h-5 transition-transform ${isFiltersExpanded ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
        
        {/* Mobile Expanded Filters */}
        {isFiltersExpanded && (
          <div className="mt-4 bg-white rounded-2xl border border-[#254e70]/10 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-[#254e70]" style={{ fontFamily: 'Kanit, sans-serif' }}>
                Filter Results
              </h3>
              <button
                onClick={handleClearAll}
                className="text-sm text-[#9dacff] hover:text-[#254e70] transition-colors"
              >
                Clear All
              </button>
            </div>
            <FilterContent />
          </div>
        )}
      </div>

      {/* Desktop Filter Sidebar */}
      <div className="hidden lg:block bg-white rounded-2xl border border-[#254e70]/10 p-6 h-fit sticky top-4">
        <div className="mb-6">
          <h2 className="text-xl font-medium text-[#254e70] mb-2" style={{ fontFamily: 'Kanit, sans-serif' }}>
            Filter Results
          </h2>
          <button
            onClick={handleClearAll}
            className="text-sm text-[#9dacff] hover:text-[#254e70] transition-colors"
          >
            Clear All Filters
          </button>
        </div>
        <FilterContent />
      </div>
    </>
  );
}

export default FilterSection; 